import { BlockReply } from "@/backend/express/controllers/blockController";
import { Block } from "@/backend/models/block";
import { BlockCreateData } from "@/types/blockTypes";
import { ContainerInterface, ContainerTypes } from "@/types/containerTypes";
import { OperationInterface } from "@/types/operationTypes";
import { create } from "zustand";
import { BlockController } from "../controllers/blockController";
import { Operation } from "@/backend/models/operation";
import { OperationController } from "../controllers/operationController";
import { Container } from "@/backend/models/containers/container";
import { ContainerController } from "../controllers/containerController";


type BlockTree = { [key: string]: BlockTreeObject };

export type BlockTreeObject = {
  id: string;
  children: BlockTreeObject[];
  type: string;
  parentId: string;
  item: Block | ContainerInterface | OperationInterface;
  wbs?: string;
};

export type BlockStore = {
  blocks: Block[],
  block: Block | null;
  tree: BlockTree;
  reset: () => void;
  listBlocks: () => Promise<void>
  listBlocksWithChildren: () => Promise<void>
  setTree: (blocks: Block[], containers: ContainerInterface[], operations: OperationInterface[]) => void;
  saveOperationAsBlock: (operation: Operation, title: string) => Promise<void>;
  saveContainerAsBlock: (container: Container, title: string) => Promise<void>;
  fetchBlock: (blockId: string) => Promise<void>;
  createBlock: (block: Omit<BlockCreateData, 'orgId'>) => Promise<void>;
  updateBlock: (block: Block, hideToast?: boolean) => Promise<BlockReply>;
  duplicateBlock: (blockId: string) => Promise<void>;
  deleteBlock: (blockId: string) => Promise<void>;
  fetchContainersBySourceId: (sourceId: string) => Promise<ContainerInterface[]>;
};

const initialState = {
  blocks: [],
  block: null,
  tree: {}
};

export const useBlockStore = create<BlockStore>((set, get) => ({
  ...initialState,
  reset: () => {
    set(initialState);
  },
  listBlocks: async () => {
    const response = await new BlockController().listBlocks();
    if (response.status === 200) {
      set({ blocks: response.blocks?.map((block) => new Block(block)) });
    }
  },
  listBlocksWithChildren: async () => {
    const response = await new BlockController().listBlocksWithChildren();
    if (response.status === 200) {
      get().setTree(response.blocks.map((block) => new Block(block)), response.containers, response.operations);
    }
  },
  setTree(blocks, containers, operations) {
    set({ blocks });
    set({ tree: buildChildLookup(blocks, containers, operations) });
  },
  saveOperationAsBlock: async (operation, title) => {
    await new OperationController().saveOperationAsBlock({ operation, blockTitle: title });
    void get().listBlocksWithChildren();
  },
  saveContainerAsBlock: async (container, title) => {
    await new ContainerController().saveBlock({ container, blockTitle: title });
    void get().listBlocksWithChildren();
  },
  fetchBlock: async (blockId: string) => {
    const response = await new BlockController().readBlock(blockId);
    if (response.status === 200 && response.block) {
      set({ block: new Block(response.block) });
    }
  },
  createBlock: async (block) => {
    const response = await new BlockController().createBlock({ block });
    if (response.block) {
      const blocks = [...get().blocks, new Block(response.block)];
      set({ blocks });
    }
  },
  updateBlock: async (block) => {
    const response = await new BlockController().updateBlock({ block });
    const updatedBlock = response.block;
    if (response.status === 200 && updatedBlock) {
      const blocks = get().blocks.map((storeBlock) => (updatedBlock.id === storeBlock.id ? new Block(updatedBlock) : storeBlock));
      set({ blocks, block: new Block(updatedBlock) });
    }
    return response;
  },
  duplicateBlock: async (blockId) => {
    const response = await new BlockController().duplicateBlock(blockId);
    if (response.status === 200 && response.block) {
      const blocks = [...get().blocks, new Block(response.block)];
      set({ blocks });
    }
  },
  deleteBlock: async (blockId) => {
    const response = await new BlockController().deleteBlock(blockId);
    if (response.status === 200) {
      const blocks = get().blocks.filter((block) => block.id !== blockId);
      set({ blocks });
    }
  },
  fetchContainersBySourceId: async (sourceId: string) => {
    const response = await new ContainerController().readBySourceId(sourceId);
    if (response.status !== 200) {
      return [];
    }
    return response.containers;
  },
}));

function buildChildLookup(blocks: Block[], containers: ContainerInterface[], operations: OperationInterface[]) {
  const fullLookup: BlockTree = {};
  const tree: BlockTree = {};

  blocks.forEach((block) => {
    const blockNode = { id: block.id, title: block.title, children: [], type: 'block', parentId: '', item: block };
    fullLookup[block.id] = blockNode;
    tree[block.id] = blockNode;
  });

  containers.forEach((container) => {
    fullLookup[container.id] = { id: container.id, children: [], type: container.containerType, parentId: container.parentId, item: container };
  });

  operations.forEach((operation) => {
    fullLookup[operation.parentId].children.push({ id: operation.id, children: [], type: operation.type, parentId: operation.parentId, item: operation });
  });

  containers.forEach((item) => {
    const currentNode = fullLookup[item.id];
    if (currentNode) {
      fullLookup[currentNode.parentId].children.push(currentNode);
    }
  });

  blocks.forEach(block => {
    const currentNode = fullLookup[block.id];
    if (currentNode) {
      setWBS(currentNode.children, '');
    }
  });

  return tree;
}

function setWBS(nodes: BlockTreeObject[], parentWBS?: string) {
  nodes.filter(node => node.type === ContainerTypes.Section).forEach((node, index) => {
    const wbs = `${parentWBS}${index + 1}`;
    node.wbs = wbs;
    setWBS(node.children, `${wbs}.`);
  });
}
